import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageSkeleton } from "@/components/PageSkeleton";
import { ShieldAlert, Check, X, Trash2, XCircle, ChevronDown, ChevronUp } from "lucide-react";

export default function DeletionApprovals() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState("");
  const [cancelRows, setCancelRows] = useState(null);

  const load = () => {
    api.get(`/deletion-requests?status=${tab}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get(`/cancellation-requests?status=${tab}`).then((r) => setCancelRows(r.data)).catch(() => setCancelRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/deletion-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Permintaan disetujui, data dihapus" : "Permintaan ditolak");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const actCancel = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/cancellation-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Pembatalan disetujui & diproses" : "Permintaan pembatalan ditolak");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const fmtMoney = (n) => `Rp ${Math.round(n || 0).toLocaleString("id-ID")}`;
  const actionLabel = { titipan: "Jadikan Titipan", refund: "Refund Uang", transfer: "Alihkan ke Order Lain" };
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const OrderDetail = ({ s, tid }) => (
    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 rounded-lg bg-secondary/50 p-2.5 text-[11px]" data-testid={tid}>
      {!s && <span className="col-span-3 text-muted-foreground italic">Rincian order tidak tersedia (order sudah terhapus).</span>}
      {s && (<>
        <span className="text-muted-foreground">Pelanggan: <b className="text-foreground">{s.customer_name || "-"}</b></span>
        <span className="text-muted-foreground">Vendor: <b className="text-foreground">{s.supplier_name || "-"}</b></span>
        <span className="text-muted-foreground">Admin: <b className="text-foreground">{s.created_by_name || "-"}</b></span>
        <span className="text-muted-foreground">Kategori: <b className="text-foreground">{s.category || "-"}</b></span>
        <span className="text-muted-foreground">Jumlah: <b className="text-foreground">{s.quantity ?? "-"}</b></span>
        <span className="text-muted-foreground">Status Bayar: <b className="text-foreground">{s.payment_status || "-"}</b></span>
        <span className="text-muted-foreground">Harga Jual: <b className="text-foreground">{fmtMoney(s.unit_sell)}</b></span>
        <span className="text-muted-foreground">Harga Modal: <b className="text-foreground">{fmtMoney(s.unit_base)}</b></span>
        <span className="text-muted-foreground">Cashback: <b className="text-foreground">{fmtMoney(s.cashback)}</b></span>
        <span className="text-muted-foreground">Total Order: <b className="text-foreground">{fmtMoney(s.amount)}</b></span>
        <span className="text-muted-foreground">Terbayar: <b className="text-foreground">{fmtMoney(s.customer_paid)}</b></span>
        <span className="text-muted-foreground">Uang Muka: <b className="text-foreground">{fmtMoney(s.down_payment)}</b></span>
        <span className="text-muted-foreground">Tgl Order: <b className="text-foreground">{s.date ? formatDate(s.date) : "-"}</b></span>
        <span className="text-muted-foreground">Tgl Kirim: <b className="text-foreground">{s.delivery_date ? formatDate(s.delivery_date) : "-"}</b></span>
        <span className="text-muted-foreground">Status Order: <b className="text-foreground">{s.order_status || "-"}</b></span>
      </>)}
    </div>
  );

  if (!rows) return <PageSkeleton stats={0} rows={5} />;

  return (
    <div className="space-y-4">
      <div data-testid="deletion-approvals-header">
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-amber-500" /> Persetujuan
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Setujui atau tolak permintaan hapus data & pembatalan order yang diajukan admin.</p>
      </div>

      <div className="flex gap-1.5">
        {["pending", "approved", "rejected", "all"].map((t) => (
          <Button key={t} size="sm" variant={tab === t ? "gold" : "outline"} className="h-8 text-xs capitalize" onClick={() => setTab(t)} data-testid={`del-tab-${t}`}>
            {t === "pending" ? "Menunggu" : t === "approved" ? "Disetujui" : t === "rejected" ? "Ditolak" : "Semua"}
          </Button>
        ))}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Pembatalan Order</p>
        <Card className="divide-y divide-border" data-testid="cancel-approvals-list">
          {(cancelRows || []).length === 0 && <div className="text-center text-muted-foreground py-8 text-sm" data-testid="cancel-empty">Tidak ada permintaan pembatalan.</div>}
          {(cancelRows || []).map((r) => (
            <div key={r.id} data-testid={`cancel-req-${r.id}`} className="p-3.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px] gap-1 text-rose-600 border-rose-300"><XCircle className="h-3 w-3" /> Pembatalan</Badge>
                  <span className="font-medium text-sm truncate">{r.order_no} · {r.customer_name}</span>
                  <Badge variant="outline" className="text-[10px]">{actionLabel[r.money_action] || r.money_action}</Badge>
                  {r.status !== "pending" && (
                    <Badge variant="outline" className={`text-[10px] ${r.status === "approved" ? "text-emerald-600 border-emerald-300" : "text-rose-600 border-rose-300"}`}>
                      {r.status === "approved" ? "Disetujui" : "Ditolak"}
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Total {fmtMoney(r.amount)} · dibayar {fmtMoney(r.paid_amount)}
                  {r.money_action === "transfer" && r.target_order_no ? ` → ${r.target_order_no}` : ""}
                  {r.reason ? ` · "${r.reason}"` : ""}
                </p>
                <p className="text-[11px] text-muted-foreground">Diajukan oleh {r.requested_by_name} · {formatDate(r.created_at)}{r.resolved_by ? ` · diproses ${r.resolved_by}` : ""}</p>
                <button type="button" onClick={() => toggle(`c-${r.id}`)} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 dark:text-sky-400" data-testid={`cancel-detail-toggle-${r.id}`}>
                  {expanded[`c-${r.id}`] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Rincian Order
                </button>
                {expanded[`c-${r.id}`] && <OrderDetail s={r.snapshot} tid={`cancel-detail-${r.id}`} />}
              </div>
              {r.status === "pending" && (
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="gold" className="h-8 text-xs gap-1" disabled={busy === r.id} onClick={() => actCancel(r.id, "approve")} data-testid={`cancel-approve-${r.id}`}>
                    <Check className="h-3.5 w-3.5" /> Setujui
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-rose-600" disabled={busy === r.id} onClick={() => actCancel(r.id, "reject")} data-testid={`cancel-reject-${r.id}`}>
                    <X className="h-3.5 w-3.5" /> Tolak
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Card>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Hapus Data</p>
        <Card className="divide-y divide-border">
        {rows.length === 0 && <div className="text-center text-muted-foreground py-12 text-sm" data-testid="del-empty">Tidak ada permintaan.</div>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`del-req-${r.id}`} className="p-3.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px] gap-1"><Trash2 className="h-3 w-3" /> {r.category}</Badge>
                <span className="font-medium text-sm truncate">{r.label}</span>
                {r.status !== "pending" && (
                  <Badge variant="outline" className={`text-[10px] ${r.status === "approved" ? "text-emerald-600 border-emerald-300" : "text-rose-600 border-rose-300"}`}>
                    {r.status === "approved" ? "Disetujui" : "Ditolak"}
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Diajukan oleh {r.requested_by_name} · {formatDate(r.created_at)}
                {r.resolved_by ? ` · diproses ${r.resolved_by}` : ""}
              </p>
              {r.resource_type === "transaction" && (
                <>
                  <button type="button" onClick={() => toggle(`d-${r.id}`)} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-600 dark:text-sky-400" data-testid={`del-detail-toggle-${r.id}`}>
                    {expanded[`d-${r.id}`] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Rincian Order
                  </button>
                  {expanded[`d-${r.id}`] && <OrderDetail s={r.snapshot} tid={`del-detail-${r.id}`} />}
                </>
              )}
            </div>
            {r.status === "pending" && (
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" variant="gold" className="h-8 text-xs gap-1" disabled={busy === r.id} onClick={() => act(r.id, "approve")} data-testid={`del-approve-${r.id}`}>
                  <Check className="h-3.5 w-3.5" /> Setujui
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-rose-600" disabled={busy === r.id} onClick={() => act(r.id, "reject")} data-testid={`del-reject-${r.id}`}>
                  <X className="h-3.5 w-3.5" /> Tolak
                </Button>
              </div>
            )}
          </div>
        ))}
      </Card>
      </div>
    </div>
  );
}
